import Header from "../components/Header";
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { Box, Breadcrumbs, Button, Card, Chip, Divider, Link, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatToBrl } from "../../utils/FormatToBrl";
import EditarPlano from "./components/EditarPlano";

const corPrimaria = "#0b243d";
const LIMITE_PLANOS = 4;

// Preço atual com o valor antigo riscado e o desconto ao lado
function Preco({ titulo, valorAntigo, valorNovo, desconto, sufixo }) {
    const percentual = parseInt(desconto) || 0;
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>
                {titulo}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: corPrimaria }}>{formatToBrl(valorNovo)}</Typography>
                <Typography variant="body2" color="text.secondary">{sufixo}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 24 }}>
                {Number(valorAntigo) > Number(valorNovo) && (
                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {formatToBrl(valorAntigo)}
                    </Typography>
                )}
                {percentual > 0 && (
                    <Chip label={`-${percentual}%`} size="small" color="success" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
                )}
            </Box>
        </Box>
    );
}

function Planos() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [planos, setPlanos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [editarPlano, setEditarPlano] = useState(false);
    const [idPlano, setIdPlano] = useState(null);

    useEffect(() => {
        const getPlanos = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarPlanos`)
                setPlanos(response.data.planos || []);
                console.log(response)
            } catch (err) {
                console.error(err);
            } finally {
                setCarregando(false);
            }
        }

        getPlanos();
    }, [token, navigate, editarPlano])

    const handleAdicionarPlano = async () => {
        if (planos.length >= LIMITE_PLANOS) {
            alert("Limite de 4 planos atingido.");
        }
    }

    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Planos</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mt: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Planos</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Preços, descontos e limites de NFS-e oferecidos aos clientes · {planos.length}/{LIMITE_PLANOS} planos
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            sx={{ backgroundColor: corPrimaria, textTransform: "none", "&:hover": { backgroundColor: "#153d66" } }}
                            onClick={handleAdicionarPlano}
                        >
                            Adicionar Plano
                        </Button>
                    </Box>

                    <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
                        {carregando ? (
                            Array.from({ length: LIMITE_PLANOS }).map((_, i) => <Skeleton key={i} variant="rounded" height={420} />)
                        ) : planos.length === 0 ? (
                            <Card variant="outlined" sx={{ borderRadius: 2, p: 4, textAlign: "center", gridColumn: "1 / -1" }}>
                                <Typography sx={{ fontWeight: 500 }}>Nenhum plano cadastrado</Typography>
                            </Card>
                        ) : (
                            planos.map((plano) => (
                                <Card key={plano?.idPlano} variant="outlined" sx={{ borderRadius: 2, display: "flex", flexDirection: "column", opacity: plano?.ativo === false ? 0.7 : 1 }}>
                                    <Box sx={{ p: 2.5, pb: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{plano?.nome}</Typography>
                                            <Typography variant="caption" color="text.secondary">#{plano?.idPlano}</Typography>
                                        </Box>
                                        {plano?.ativo === false && <Chip label="Inativo" size="small" sx={{ mt: 0.5, height: 20, fontSize: 11 }} />}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, minHeight: 40 }}>
                                            {plano?.descricao || "Sem descrição"}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Preco titulo="Mensal" valorAntigo={plano?.valorAntigoMensal} valorNovo={plano?.valorNovoMensal} desconto={plano?.descontoMensal} sufixo="/mês" />
                                        <Preco titulo="Anual" valorAntigo={plano?.valorAntigoAnual} valorNovo={plano?.valorNovoAnual} desconto={plano?.descontoAnual} sufixo="/ano" />
                                    </Box>

                                    <Divider />

                                    <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>
                                            NFS-e por mês
                                        </Typography>
                                        {[
                                            { label: "Emitidas pelo usuário", valor: plano?.qtdNfseMensalUsuario },
                                            { label: "Emitidas pela Clarea", valor: plano?.qtdNfseMensalClarea },
                                        ].map((item) => (
                                            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <ReceiptLongOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                                <Typography variant="body2" sx={{ flex: 1 }}>{item.label}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.valor ?? "—"}</Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box sx={{ p: 2.5, pt: 0, mt: "auto" }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            sx={{ textTransform: "none", color: corPrimaria, borderColor: "divider" }}
                                            onClick={() => {
                                                setIdPlano(plano?.idPlano);
                                                setEditarPlano(true);
                                            }}
                                        >
                                            Editar plano
                                        </Button>
                                    </Box>
                                </Card>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
            <EditarPlano editarPlano={editarPlano} setEditarPlano={setEditarPlano} idPlano={idPlano} />
        </div>
    );
}

export default Planos;
